import { useMemo, useState } from 'react'

import { SectionHeading } from '@/components/ui'
import type { DateRange } from '@/components/ui/Calendar'
import { useProject } from '@/modules/projects'
import { categoryColors, chartPalette } from '@/utils'
import type { CategoryBreakdownResult } from '../hooks'
import { CategoryChart } from './CategoryChart'

interface CategoriesInsightsProps {
  breakdown: CategoryBreakdownResult
}

export function CategoriesInsights({ breakdown }: CategoriesInsightsProps) {
  const project = useProject()
  const [categoryDateRange, setCategoryDateRange] = useState<
    DateRange | undefined
  >(undefined)
  const [detailDateRange, setDetailDateRange] = useState<DateRange | undefined>(
    undefined,
  )

  const detailCategoryColorMap = useMemo(() => {
    const colorMap: Record<string, string> = {}
    for (const [index, categoryName] of breakdown.detailCategories.entries()) {
      colorMap[categoryName] = chartPalette[index % chartPalette.length]
    }
    return colorMap
  }, [breakdown.detailCategories])

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Category breakdown"
        description="Transaction totals by category, split by inflows and outflows"
      />

      <CategoryChart
        title="By category"
        contextId="insights-categories-by-category"
        categoryTotals={breakdown.categoryTotals}
        monthlyByCategory={breakdown.monthlyByCategory}
        colorMap={categoryColors}
        dateRange={categoryDateRange}
        onDateRangeChange={setCategoryDateRange}
        initialDate={breakdown.dataDateRange?.from}
        finalDate={breakdown.dataDateRange?.to}
        projectName={project?.name}
        tourTarget="category-chart"
      />

      <CategoryChart
        title="By detail category"
        contextId="insights-categories-by-detail"
        categoryTotals={breakdown.detailCategoryTotals}
        monthlyByCategory={breakdown.monthlyByDetailCategory}
        colorMap={detailCategoryColorMap}
        dateRange={detailDateRange}
        onDateRangeChange={setDetailDateRange}
        initialDate={breakdown.dataDateRange?.from}
        finalDate={breakdown.dataDateRange?.to}
        projectName={project?.name}
        tickSpacing={20}
      />
    </div>
  )
}
